import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Box,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Flex,
  Text,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useSwrStale } from '../lib/hooks';
import {
  useAppdata,
  useForestExpert,
  useTranslation,
} from '../lib/hooks.context';
import { MhyChakraProps, Person } from '../types';
import { Option } from './AutoSuggest';
import Image from './Image';

const AutoSuggest = dynamic(() => import('./AutoSuggest'));

interface Props extends MhyChakraProps {
  isActive?: boolean;
  allowReset?: boolean;
}

const PersonItem = ({
  firstname,
  surname,
  image_src_md,
  office,
  territory,
  searchTerm,
  blogname,
}: Person & { searchTerm: string; blogname?: string }) => {
  const areas = territory.filter((t) => new RegExp(searchTerm, 'i').test(t));

  return (
    <Flex py="2" px="1">
      <Box
        flex="0 0 36px"
        maxWidth="36px"
        height="45px"
        backgroundColor="gray2"
        borderRadius="lg"
      >
        {image_src_md && (
          <Image
            width="36"
            height="45"
            src={image_src_md}
            useUnoptimizedImage
            alt={`${firstname} ${surname}`}
          />
        )}
      </Box>
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="start"
        gridRowGap={1}
        ml="2"
      >
        <Text lineHeight={1}>
          {firstname} {surname}
        </Text>
        <Text lineHeight={1}>
          <Text as="span">{blogname || ''}</Text>
          <Text as="span">{office[0]}</Text>
        </Text>
        {areas?.length > 0 && (
          <Text
            color="greenSpruce"
            lineHeight={1.25}
            flexFlow="row wrap"
            display="inline-flex"
          >
            {areas.map((area, i) => (
              <Text key={area} as="span" mr={1}>
                {area}
                {areas.length !== i + 1 ? (
                  <span aria-hidden="true">,</span>
                ) : null}
              </Text>
            ))}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

const peopleToOptions = (
  people: Person[],
  blogTags: string[] = [],
  blogname?: string,
): Option[] =>
  people.map(
    ({ id, firstname, surname, territory, office, ...restPerson }) => ({
      id,
      name: `${firstname || ''} ${surname || ''}`.trim(),
      value: id.toString(),
      tags: blogname ? [...territory, ...blogTags, blogname] : territory,
      office,
      node: (searchTerm) => (
        <PersonItem
          key={id}
          {...restPerson}
          {...{
            id,
            territory,
            firstname,
            surname,
            searchTerm,
            blogname,
            office,
          }}
        />
      ),
    }),
  );

const ForestExpertSearch = ({
  isActive: isActiveByDefault = false,
  allowReset = false,
  ...rest
}: Props) => {
  const { locale = 'fi' } = useRouter();
  const { subsite: currentSubsite } = useAppdata();
  const [{ isActive }, setState] = useState({
    isActive: isActiveByDefault,
    subsite: currentSubsite,
  });
  const { forestExpert, setForestExpert } = useForestExpert();
  const { data: people } = useSwrStale<Person[]>(
    // eslint-disable-next-line no-nested-ternary
    isActive
      ? currentSubsite
        ? `/entities/experts?sites=${currentSubsite}`
        : `/entities/experts?sites=all&lang=${locale}`
      : null,
  );
  const t = useTranslation();

  const router = useRouter();

  useEffect(() => {
    setState((s) => ({ ...s, isActive: false }));
  }, [router.query.entry, forestExpert]);

  if (forestExpert) {
    if (allowReset) {
      return (
        <Button
          {...rest}
          variant="solidGreen"
          size="large"
          onClick={() => {
            setForestExpert(null);
          }}
        >
          {t('forest_expert_remove')}
        </Button>
      );
    }
    return null;
  }

  if (!isActive) {
    return (
      <Button
        {...rest}
        variant="solidGreen"
        size="large"
        onClick={() => {
          setState((s) => ({ ...s, isActive: true }));
        }}
        whiteSpace="initial"
        height="auto"
      >
        {t('forest_expert_find_title')}
      </Button>
    );
  }

  const onSelectForestExpert = (option: Option) => {
    setForestExpert((people as Person[]).find(({ id }) => id === option.id));
    setState((s) => ({ ...s, subsite: currentSubsite }));
  };

  let items: Option[] = [];
  if ((people as Person[])?.length) {
    items = peopleToOptions(people as Person[]);
  }

  return (
    <AutoSuggest
      items={items || []}
      initialItems={currentSubsite ? items : undefined}
      onAdd={onSelectForestExpert}
      maxWidth="400px"
    >
      {({ getOuterProps, getInputProps, openMenu }) => (
        <InputGroup
          {...getOuterProps()}
          display="inline-flex"
          width="auto"
          size="lg"
        >
          <InputLeftElement pointerEvents="none" color="highlightColor">
            <MagnifyingGlass size="18" />
          </InputLeftElement>
          <Input
            variant="search"
            pr="8"
            pl="10"
            {...getInputProps({
              id: 'forest-expert-search',
              placeholder: t('forest_expert_search_placeholder'),
              onFocus: openMenu,
            })}
          />
          {!people ? (
            <InputRightElement>
              <Button as="span" variant="transparent" isLoading />
            </InputRightElement>
          ) : null}
        </InputGroup>
      )}
    </AutoSuggest>
  );
};

export default ForestExpertSearch;
